import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Paper, Typography, Button, TextField, Box, Select, MenuItem, InputLabel, FormControl, TableSortLabel } from '@mui/material';
import { addAchievementAsync, updateAchievementAsync, selectAchievement, 
  clearSelectedAchievement, fetchAchievements, deleteAchievementAsync,
  fetchAchievementsListUsers, fetchAchievementsListTeams } from '../app/achievementsSlice';
import Debug from '../utils/logger';
import Table from '@msi/cobalt-react/table';

const debug = new Debug('PanelAchievements');
debug.enabled = true;
debug.enableActions({'useEffect': true});
  

const PanelAchievements = () => {
  const dispatch = useDispatch();
  const achievements = useSelector((state) => state.achievements.achievements);
  const selectedAchievement = useSelector((state) => state.achievements.selectedAchievement);
  const users = useSelector((state) => state.users.users);
  const scoring = useSelector((state) => state.scoring.scoring);
  const badges = useSelector((state) => state.scoring.badges);
  const selectedRole = useSelector((state) => state.login.selectedRole);
  const coreId = useSelector((state) => state.login.coreId || '');
  const [formValues, setFormValues] = useState({ coreId: '', actionId: '', badgeId: '', date: new Date().toISOString().slice(0, 10), comment: '' });
  const [showForm, setShowForm] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const isDarkMode = useSelector((state) => state.theme.mode === 'dark');

  const [calledForAchievementsTs, setCalledForAchievementsTs] = useState(0);

  const MinInterval = 1000;

  useEffect(() => {
    let nowTs = Date.now();
    let delta = (calledForAchievementsTs > 0) ? (nowTs - calledForAchievementsTs) : MinInterval;
    debug.log('useEffect', 'PanelAchievements useEffect now, prev, delta:', nowTs, calledForAchievementsTs, delta);
    debug.log('useEffect', 'PanelAchievements condition: ',(calledForAchievementsTs === 0) || (delta >= MinInterval));
    if ((calledForAchievementsTs === 0) || (delta >= MinInterval)) {
      if(selectedRole === 'User') {
        debug.log('useEffect', 'PanelAchievements fetchAchievements coreId:', coreId);
        let listCoreIds = [coreId];
        dispatch(fetchAchievementsListUsers(listCoreIds));
      } else if(selectedRole === 'Manager') {
        let listCoreIds = [coreId];
        debug.log('useEffect', 'PanelAchievements fetchAchievementsListUsers listCoreIds:', listCoreIds);
        dispatch(fetchAchievementsListTeams(listCoreIds));
      } else {
        debug.log('useEffect', 'PanelAchievements fetchAchievements All');
        dispatch(fetchAchievements());
      }
      setCalledForAchievementsTs(nowTs);
    }
  }, [dispatch, selectedRole, coreId, users]);

  useEffect(() => {
    if (selectedAchievement) {
      setFormValues({ ...selectedAchievement, date: new Date(selectedAchievement.date).toISOString().slice(0, 10) });
      setShowForm(true);
    } else {
      setFormValues({ coreId: '', actionId: '', badgeId: '', date: new Date().toISOString().slice(0, 10), comment: '' });
      setShowForm(false);
    }
  }, [selectedAchievement]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if(name === 'achievementId') newValue = value.toUpperCase();
    if(name === 'coreId') newValue = value.toLowerCase();
    setFormValues({ ...formValues, [name]: newValue });
  };

  const handleSubmit = () => {
    if (!formValues.date || !formValues.coreId) {
      return;
    }
    if (selectedAchievement) {
      dispatch(updateAchievementAsync({ ...formValues, id: selectedAchievement.id, date: new Date(formValues.date) }));
    } else {
      dispatch(addAchievementAsync({ ...formValues, date: new Date(formValues.date) }));
    }
    setFormValues({ coreId: '', actionId: '', badgeId: '', date: new Date().toISOString().slice(0, 10), comment: '' });
    dispatch(clearSelectedAchievement());
    setShowForm(false);
  };

  const handleCancel = () => {
    setFormValues({ coreId: '', actionId: '', badgeId: '', date: new Date().toISOString().slice(0, 10), comment: '' });
    dispatch(clearSelectedAchievement());
    setShowForm(false);
  };

  const handleDelete = () => {
    if (selectedAchievement) {
      dispatch(deleteAchievementAsync(selectedAchievement.id));
      setFormValues({ coreId: '', actionId: '', badgeId: '', date: new Date().toISOString().slice(0, 10), comment: '' });
      dispatch(clearSelectedAchievement());
      dispatch(fetchAchievements());
      setShowForm(false);
    }
  };

  const handleRowClick = (achievement) => {
    dispatch(selectAchievement(achievement));
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedAchievements = achievements.slice().sort((a, b) => {
    if (orderBy === 'date') {
      return order === 'asc' ? new Date(a[orderBy]) - new Date(b[orderBy]) : new Date(b[orderBy]) - new Date(a[orderBy]);
    } else if(orderBy === 'badgeId') {
      var indexA = badges.findIndex(badge => badge.id === a[orderBy]);
      var indexB = badges.findIndex(badge => badge.id === b[orderBy]);
      return order === 'asc' ? (indexA > indexB ? -1 : 1) : (indexA < indexB ? -1 : 1);
    } else {
      return order === 'asc' ? (a[orderBy] < b[orderBy] ? -1 : 1) : (a[orderBy] > b[orderBy] ? -1 : 1);
    }
  });

  const rawUsers = (users && Array.isArray(users) && users.length) ? users : [];

  const sortedUsers = rawUsers ? [...rawUsers].sort((a, b) => `${a.name} (${a.coreId.toUpperCase()})`.localeCompare(`${b.name} (${b.coreId.toUpperCase()})`)) : [];

  return (
    <Box sx={{ backgroundColor: (theme) => theme.palette.secondary.main, color: (theme) => theme.palette.text.main, padding: 2 }}>
      <Typography variant="h4">Levels</Typography>
      {showForm && (
        <Box component="form" sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <FormControl fullWidth margin="normal" size="small" sx={{ maxWidth: 500 }}>
              <InputLabel shrink sx={{ backgroundColor: 'white', px: 0.5 }}>User</InputLabel>
              <Select name="coreId" value={formValues.coreId} onChange={handleInputChange} disabled={selectedAchievement ? true : false}>
                {sortedUsers.map((user) => (
                  <MenuItem key={user.coreId} value={user.coreId} sx={{ textAlign: 'left' }}>{`${user.name} (${user.coreId.toUpperCase()})`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" size="small" sx={{ maxWidth: 500 }}>
              <InputLabel shrink sx={{ backgroundColor: 'white', px: 0.5 }}>Action</InputLabel>
              <Select name="actionId" value={formValues.actionId} onChange={handleInputChange}>
                {scoring?.map((stage) => (
                  <MenuItem key={stage.id} value={stage.id} sx={{ textAlign: 'left' }}>{stage.stage}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" size="small" sx={{ maxWidth: 500 }}>
              <InputLabel shrink sx={{ backgroundColor: 'white', px: 0.5 }}>Badge</InputLabel>
              <Select name="badgeId" value={formValues.badgeId} onChange={handleInputChange}>
                <MenuItem value="" sx={{ textAlign: 'left' }}>None</MenuItem>
                {badges?.map((badge) => (
                  <MenuItem key={badge.id} value={badge.id} sx={{ textAlign: 'left' }}>{badge.badge}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField name="date" label="Date" type="date" value={formValues.date} onChange={handleInputChange} fullWidth margin="normal" InputLabelProps={{ shrink: true }} size="small" sx={{ mb: 1, maxWidth: 500 }} />
            <TextField name="comment" label="Comment" value={formValues.comment} onChange={handleInputChange} fullWidth margin="normal" size="small" sx={{ mb: 1, maxWidth: 500 }} />
            <Box sx={{ display: 'flex', justifyContent:'space-between', flexDirection: 'row', maxWidth: '500px' }}>
              <Button variant="contained" color="primary" onClick={handleSubmit} size="small">{selectedAchievement ? 'Update' : 'Submit'}</Button>
              <Button variant="contained" color="secondary" onClick={handleCancel} size="small">Cancel</Button>
              {selectedAchievement && <Button variant="contained" color="error" onClick={handleDelete} size="small">Delete</Button>}
            </Box>
          </Box>
        </Box>
      )}
      {(achievements && (
        <Box display='inline-block' sx={{ mt: 2 }}>
          <Paper>
            <Table columns={[
              {
                field: 'id',
                header: 'Item ID',
                onSortClick: () => handleRequestSort(null, 'id'),
                sortDirection: orderBy === 'id' ? order : null
              },
              {
                field: 'userName',
                header: 'User Name',
                onSortClick: () => handleRequestSort(null, 'userName'),
                sortDirection: orderBy === 'userName' ? order : null
              },
              {
                field: 'coreId',
                header: 'Core ID',
                onSortClick: () => handleRequestSort(null, 'coreId'),
                sortDirection: orderBy === 'coreId' ? order : null
              },
              {
                field: 'badgeId',
                header: 'Reward Id',
                onSortClick: () => handleRequestSort(null, 'badgeId'),
                sortDirection: orderBy === 'badgeId' ? order : null
              },
              {
                field: 'date',
                header: 'Date',
                onSortClick: () => handleRequestSort(null, 'date'),
                sortDirection: orderBy === 'date' ? order : null
              },
              {
                field: 'image',
                header: 'Level Reward',
                onSortClick: () => handleRequestSort(null, 'badgeId'),
                sortDirection: orderBy === 'badgeId' ? order : null
              }
            ]}
            tableContent={sortedAchievements.map((achievement) => {
              const user = rawUsers?.find(u => u.coreId === achievement.coreId);
              return {
                id: achievement.id,
                userName: user ? user.name : 'Unknown',
                coreId: achievement.coreId?.toUpperCase() || '',
                badgeId: achievement.badgeId || 'N/A',
                date: new Date(achievement.date).toLocaleDateString(),
                image: achievement.badgeId ? <img src={`/${achievement.badgeId}.png`} alt={achievement.badgeId} style={{width: '50px', height: 'auto' }}  /> : 'N/A'
              };
            })}       
            />   
          </Paper>
        </Box>
      )) || (<Typography>No Activity Found.</Typography>)}
    </Box>
  );
};

export default PanelAchievements;

